import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { withStyles, CardTabs, BodyText, Box, webhookEventList } from 'vopay';
import { Title, UtilButton } from '#shared/components/UI';
import { useDialog } from '#shared/providers/Dialog';
import HowWebhooksWorkDialog from './HowWebhooksWorkDialog';
import UpdateWebhooksPage from './UpdateWebhooks/UpdateWebhooksPage';
import WebhooksHistoryPage from './WebhookHistory/WebhooksHistoryPage';
import { WebhooksHostProps } from './types';
import styles from './WebhooksPage.module.scss';

const cn = withStyles(styles);

export default function WebhooksPage({
  basePath,
  viewer,
  embedded = false,
  readOnly = false,
  events = webhookEventList,
  hasDeliveryDetails = true,
}: WebhooksHostProps) {
  const dialog = useDialog();
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.Webhooks' });

  const historyPath = `${basePath}/history`;
  const activeTab = location.pathname === historyPath ? 'history' : 'manage';

  function handleShowHowItWorks() {
    dialog.open<void>(({ onCancel }) => <HowWebhooksWorkDialog onClose={onCancel} />, { bare: true });
  }

  function handleTabChange(value: string) {
    history.push(value === 'history' ? historyPath : basePath);
  }

  return (
    <Box flx="col" gapSize="20px">
      {embedded ? (
        <Title level="3">{t('pageTitle', { defaultValue: 'Webhooks' })}</Title>
      ) : (
        <>
          <Box flx="col" gapSize="8px">
            <Title>{t('pageTitle', { defaultValue: 'Webhooks' })}</Title>

            <BodyText className={cn('description')}>
              {t('description', {
                defaultValue:
                  'Get notified in real time when something changes in your account. Enable the events you care about and tell us where to send them.',
              })}
            </BodyText>
          </Box>

          <UtilButton iconRight="info" className={cn('howItWorks')} onClick={handleShowHowItWorks}>
            {t('howItWorksButton', { defaultValue: 'How Webhooks Work' })}
          </UtilButton>
        </>
      )}

      <CardTabs.Root value={activeTab} onValueChange={handleTabChange}>
        <CardTabs.List aria-label={t('pageTitle', { defaultValue: 'Webhooks' })}>
          <CardTabs.Trigger value="manage">
            {t('Tabs.manageWebhooks', { defaultValue: 'Manage Webhooks' })}
          </CardTabs.Trigger>

          <CardTabs.Trigger value="history">
            {t('Tabs.webhooksHistory', { defaultValue: 'Webhooks History' })}
          </CardTabs.Trigger>
        </CardTabs.List>

        <CardTabs.Content value="manage">
          <UpdateWebhooksPage
            username={viewer.username}
            events={events}
            readOnly={readOnly}
            onShowHistory={() => history.push(historyPath)}
          />
        </CardTabs.Content>

        <CardTabs.Content value="history">
          <WebhooksHistoryPage
            timezone={viewer.timezone}
            events={events}
            hasDeliveryDetails={hasDeliveryDetails}
            readOnly={readOnly}
          />
        </CardTabs.Content>
      </CardTabs.Root>
    </Box>
  );
}
